// React and third-parties
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';

// Components
import { TableMoneyFormat } from './TableFormats';

const idFechaPagosModal = 'PagosYSafModal';

export default function PagosYSafModal(props) {

    const { id = idFechaPagosModal, isPagos, pagos, open, parentInfo, onCloseModal } = props;

    const [currentPagos, setCurrentPagos] = useState(pagos);
    const [scrollPosition, setScrollPosition] = useState('scroll-top');

    const { id: parentId, rect } = parentInfo;

    if (currentPagos !== pagos && pagos && open) {
        setCurrentPagos(pagos);
        setScrollPosition('scroll-top');
    }
   
    useEffect(() => {

        if (!open) return;

        const onPointerEnter = (e) => {

            const pagosTd = document.getElementById(parentId);
            const fechaPagosModal = document.getElementById(id);

            const tdContains = pagosTd.contains(e.target);
            const modalContains = fechaPagosModal.contains(e.target);      
           
            const inHoverArea = tdContains || modalContains;

            if(!inHoverArea) onCloseModal();
        };

        document.addEventListener('pointerover', onPointerEnter);
        return () => document.removeEventListener('pointerover', onPointerEnter);

    }, [open, id, parentId, onCloseModal]);
    
    return (
        <FechaPagosModal 
            id={id}
            open={open}
            scrollPosition={scrollPosition}
            setScrollPosition={setScrollPosition}
            style={{ 
                zIndex: 2, 
                top: rect.top, left: rect.left 
            }}
        >
            {currentPagos.length > 0 ?
                isPagos ?
                    <PagosTable pagos={currentPagos}/>
                    :
                    <SafsTable pagos={currentPagos}/>
                :
                <div className='full align-center' style={{ padding: "0.1rem 0.6rem" }}>
                    <b style={{ fontSize: "0.9rem", color: "white" }}>Sin Pagos</b>
                </div>
            }
        </FechaPagosModal>
    );
}

const PagosTable = ({ pagos }) => {
    
    const hasComentarios = pagos.some(({ comentarios }) => comentarios !== "");

    return (
        <table style={{ maxWidth: 200 }}>
            <thead>
                <tr>
                    <th>Fecha</th>
                    <th>Pago</th>
                    {hasComentarios && <th>Comentarios</th>}
                </tr>
            </thead>
            <tbody>
                {pagos.map(({ fecha, pago, comentarios, disabled }, index) => (
                    <tr key={index} style={{ opacity: !disabled ? 1 : 0.5 }}>
                        <td
                            className='font-mini text-right'
                            style={{ whiteSpace: "nowrap" }}
                        >
                            {moment(fecha).locale('es').format("D MMM YYYY").replace(".", "")}
                        </td>
                        <TableMoneyFormat value={pago} />
                        {hasComentarios &&
                            <td className='font-mini'>{comentarios || "--"}</td>
                        }
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const SafsTable = ({ pagos }) => {

    return (
        <table style={{ maxWidth: 200 }}>
            <thead>
                <tr>
                    <th>Fecha</th>
                    <th>Pago</th>
                    <th>Saldo</th>
                </tr>
            </thead>
            <tbody>
                {pagos.map(({ fecha, pago, saldo, disabled }, index) => (
                    <tr key={index} style={{ opacity: !disabled ? 1 : 0.5 }}>
                        <td
                            className='font-mini text-right'
                            style={{ whiteSpace: "nowrap" }}
                        >
                            {moment(fecha).locale('es').format("D MMM YYYY").replace(".", "")}
                        </td>
                        <TableMoneyFormat value={pago} />
                        <TableMoneyFormat value={saldo} />
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

function FechaPagosModal(props) {

    const { id, open, style, scrollPosition, setScrollPosition, itemHeight = 25, maxScrollItems = 6, children } = props;

    const modalRef = useRef();

    const [modalSettings, setModalSettings] = useState({ heightOffset: 0, widthOffset: 0 });
    const [heights, setHeights] = useState({ tdHeight: undefined, scrollHeight: undefined });

    const sizeObserverHandler = (entries) => {
        if (entries[0]) {
            const { blockSize: heightOffset, inlineSize } = entries[0].borderBoxSize[0];
            const widthOffset = inlineSize;
            setModalSettings({ heightOffset, widthOffset });
        }
    };
   
    useEffect(() => {

        const observer = new ResizeObserver(sizeObserverHandler);
        observer.observe(modalRef.current, { box: "border-box" });

        return () => observer.disconnect();

    }, []);

    useEffect(() => {

        const observer = new MutationObserver((mutations) => 
            manageTableBounds(mutations, modalRef, maxScrollItems, itemHeight, setHeights));

        observer.observe(modalRef.current, { childList: true, subtree: true });

        return () => observer.disconnect();

    }, [maxScrollItems, itemHeight]);
    
    const onScroll = (e) => {

        const { scrollTop, scrollHeight, clientHeight } = e.target;

        const scrollPosition = scrollTop < 12 ? "scroll-top"
             : scrollTop + clientHeight >= scrollHeight - 12 ? "scroll-bottom" 
             : "scroll-middle";

        setScrollPosition(scrollPosition);
    };

    const visibleTopBound = heights.scrollHeight && scrollPosition !== "scroll-top" ? "visible" : "hidden";
    const visibleBottomBound = heights.scrollHeight && scrollPosition !== "scroll-bottom" ? "visible" : "hidden";
    
    const { top, left, ...restStyle } = style;

    const modal = <div
        ref={modalRef}
        id={id}
        className={'fecha-pagos-modal column ' + scrollPosition}
        style={{
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'all' : 'none',
            top: (style.top || 0) - modalSettings.heightOffset - 2,
            left: (style.left || 0) - modalSettings.widthOffset - 2,
            ...restStyle
        }}
    >
        <div className={'fecha-pagos-modal-anchor'}>
            <div 
                key={open} 
                onScroll={onScroll} 
                className='fecha-pagos-modal-scroll' style={{ height: heights.scrollHeight }}
            >
                <div className="fecha-pagos-modal-scroll-bound upper" style={{ top: heights.tdHeight, visibility: visibleTopBound }}>
                    <i className="fas fa-chevron-up upper-bound-icon" />
                </div>
                <div className="fecha-pagos-modal-scroll-bound bottom" style={{ bottom: 0, visibility: visibleBottomBound}}>
                    <i className="fas fa-chevron-down bottom-bound-icon" />
                </div>
                {children}
            </div>
        </div>
    </div>;

    return createPortal(modal, document.body);
};

const getScrollableInfo = (table, maxScrollItems, itemHeight) => {

    const theadHeight = table.querySelector('thead').clientHeight
    const maxHeight = Math.floor((maxScrollItems * itemHeight) + theadHeight);
    const isScrollable = table.clientHeight >= maxHeight;

    return { theadHeight, maxHeight, isScrollable };
};

const manageTableBounds = (mutations, modalRef, maxScrollItems, itemHeight, setHeights) => {
    
    const table = modalRef.current.querySelector('table');
    
    if (table) {

        const info = getScrollableInfo(table, maxScrollItems, itemHeight);
        const { isScrollable, maxHeight, theadHeight } = info;

        if(isScrollable) setHeights({ tdHeight: theadHeight, scrollHeight: maxHeight });
        else setHeights({ tdHeight: undefined, scrollHeight: undefined });
    } 
    else setHeights({ tdHeight: undefined, scrollHeight: undefined });
};
